import { useEffect, useState, useMemo } from 'react';
import './styles.css';
import './vehicle-detail.css';
import { Layout5Root } from './Layout5Root';
import { VehicleGallery } from './VehicleGallery';
import { useParams, Link, useLocation } from 'react-router-dom';
import { LayoutFooter as Footer, LayoutHeader as Header, LayoutVehicleCard } from '@/layouts';
import { ServiceModal } from '@/components/ServiceModal';
import { VehicleSection } from '@/components/VehicleSection';
import { extractIdFromSlug, generateVehicleSlug } from '@/lib/slug';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { openWhatsApp } from '@/lib/whatsapp';
import SEOHead from '@/components/SEOHead';
import { generateVehicleStructuredData } from '@/lib/structuredData';
import { 
  MessageCircle, 
  Heart, 
  Calendar, 
  Gauge, 
  Fuel, 
  Settings, 
  Palette,
  Car,
  Shield,
  Phone,
  MapPin,
  Building2,
  Eye,
  Star,
  CreditCard,
  Send,
  File,
  FilePen,
  Navigation
} from 'lucide-react';

import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { useToast } from '@/hooks/use-toast';
import { requestUserLocation, getDistanceToStore, formatDistance } from '@/lib/geolocation';
import { VehicleNotFound } from '@/components/VehicleNotFound';
import { trackVehicleView, trackLeadSubmission, trackWhatsAppClick, trackAddToFavorites } from '@/lib/analytics';
import { getStoreBrandAssets } from '@/lib/storeBrand';
import { getRelatedVehicles } from '@/lib/relatedVehicles';
import { openStoreDirections } from '@/lib/storeMapUrls';

// Lead Form Component (for API type and message type)
const LeadForm = ({ vehicle, config, buttonColor, store, serviceType = 'message', serviceTitle = 'Envie uma mensagem', service }: { vehicle: any; config: any; buttonColor: string; store: any; serviceType?: string; serviceTitle?: string; service?: any }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  // Pre-fill message with vehicle info on mount
  useEffect(() => {
    let vehicleInfo = '';

    if (serviceType === '65dcb7898eca108d87c91dbc') {
      // Financing service
      vehicleInfo = `Olá,\n\nGostaria de simular um financiamento para o veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nPor favor, entrem em contato para mais informações sobre as condições de financiamento.`;
    } else {
      // Default message service
      vehicleInfo = `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`;
    }

    setFormData(prev => ({
      ...prev,
      message: vehicleInfo
    }));
  }, [vehicle, serviceType]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    } else {
      return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    }
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    handleInputChange('phone', formatted);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo Nome.',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.email.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo E-mail.',
        variant: 'destructive'
      });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: '❌ E-mail inválido',
        description: 'Por favor, insira um endereço de e-mail válido.',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.phone.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo Telefone.',
        variant: 'destructive'
      });
      return false;
    }

    const phoneNumbers = formData.phone.replace(/\D/g, '');
    if (phoneNumbers.length < 10) {
      toast({
        title: '❌ Telefone inválido',
        description: 'Por favor, insira um número de telefone válido com DDD.',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.message.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo Mensagem.',
        variant: 'destructive'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Check if service has API type
      const isApiType = (service?.lead?.type === 'api' || service?.lead?.type === 'email');

      if (isApiType) {
        // Send via API
        const { LEAD } = await import('@/lib/api');

        // Prepare lead data following the correct format
        const leadData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone.replace(/\D/g, ''), // Remove formatting
          message: formData.message,
          vehicle: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ' '}${vehicle.manufactureYear}/${vehicle.modelYear}`,
          id: vehicle.id,
          plaque: vehicle.plaque || '',
          brand: vehicle.brand,
          model: vehicle.model,
          version: vehicle.version || '',
          year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
          price: formatPrice(vehicle.price),
          mileage: `${vehicle.mileage}km`
        };

        const response = await LEAD(serviceType, store, leadData);

        if (response?.status == 200) {
          // Track lead submission
          trackLeadSubmission({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            message: formData.message,
            serviceType,
            vehicle: {
              id: vehicle.id,
              brand: vehicle.brand,
              model: vehicle.model,
              version: vehicle.version || '',
              year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
              price: vehicle.price,
              mileage: vehicle.mileage
            }
          });

          toast({
            title: 'Mensagem enviada!',
            description: 'Entraremos em contato em breve.',
          });

          // Reset form
          setFormData({
            name: '',
            email: '',
            phone: '',
            message: `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`
          });
        } else{
          toast({
            title: 'Erro ao enviar',
            description: 'Tente novamente.',
            variant: 'destructive'
          });
        }
      } else {
        // Send via WhatsApp (type === 'message')
        toast({
          title: '✅ Redirecionando',
          description: 'Abrindo WhatsApp...',
        });

        const { openWhatsApp } = await import('@/lib/whatsapp');

        if (service?.lead?.ios) {
          // Build message from template (same logic as ServiceModal)
          let message = service.lead?.ios;
          const whatsappTemplate = message.substring(0, message.indexOf('&text=') + 6);
          message = message.substring(message.indexOf('&text=') + 6);

          // Prepare form data with vehicle info
          const messageData: Record<string, string> = {
            nome: formData.name,
            name: formData.name,
            email: formData.email,
            telefone: formData.phone,
            phone: formData.phone,
            mensagem: formData.message,
            message: formData.message,
            veiculo: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ''}${vehicle.manufactureYear}/${vehicle.modelYear}`,
            vehicle: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ''}${vehicle.manufactureYear}/${vehicle.modelYear}`,
            marca: vehicle.brand,
            brand: vehicle.brand,
            modelo: vehicle.model,
            model: vehicle.model,
            versao: vehicle.version || '',
            version: vehicle.version || '',
            ano: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
            year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
            preco: formatPrice(vehicle.price),
            price: formatPrice(vehicle.price),
            km: `${vehicle.mileage}km`,
            mileage: `${vehicle.mileage}km`
          };

          // Replace placeholders with form data
          Object.entries(messageData).forEach(([key, value]) => {
            const regex = new RegExp(`\\{${key}\\}`, 'g');
            message = message.replace(regex, value);
          });

          // Add current date/time if needed
          const now = new Date();
          message = message.replace(/\{datahora\}/g, now.toLocaleString('pt-BR'));
          message = message.replace(/\{datetime\}/g, now.toLocaleString('pt-BR'));

          const whatsappUrl = whatsappTemplate + encodeURIComponent(message);
          openWhatsApp(whatsappUrl);
        } else {
          // Use default WhatsApp with message
          const phoneMatch = store.whatsapp?.ios?.match(/phone=([^&]+)/);
          const phone = phoneMatch ? phoneMatch[1] : '';
          openWhatsApp(`whatsapp://send?phone=${phone}&text=${encodeURIComponent(formData.message)}`);
        }

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`
        });
      }
    } catch (error: any) {
      console.error('Error submitting lead:', error);

      toast({
        title: 'Erro ao enviar',
        description: error?.message || 'Ocorreu um erro ao enviar sua mensagem. Tente novamente.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">

        <CardTitle className="flex items-center gap-2">
          {serviceType === 'message' ? (
            <MessageCircle className="h-5 w-5 text-primary" />
          ) : (
            <CreditCard className="h-5 w-5 text-primary" />
          )}
          {serviceTitle}
        </CardTitle>

        {/* Name Field */}
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-semibold">
            Nome Completo <span className="text-red-500">*</span>
          </Label>
          <Input
            id="name"
            placeholder="Digite seu nome completo"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="h-11"
          />
        </div>

        {/* Email Field */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-semibold">
            E-mail <span className="text-red-500">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="seu@email.com"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="h-11"
          />
        </div>

        {/* Phone Field */}
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm font-semibold">
            Telefone <span className="text-red-500">*</span>
          </Label>
          <Input
            id="phone"
            type="tel"
            placeholder="(11) 99999-9999"
            value={formData.phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            maxLength={15}
            className="h-11"
          />
        </div>

        {/* Message Field */}
        <div className="space-y-2">
          <Label htmlFor="message" className="text-sm font-semibold">
            Mensagem <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="message"
            placeholder="Digite sua mensagem..."
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            rows={5}
            className="resize-none"
          />
        </div>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full h-12 text-base font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-300"
          style={{
            background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
            }
          }}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
              Enviando...
            </>
          ) : (
            <>
              <Send className="h-5 w-5 mr-2" />
              Enviar
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

const VehicleDetail = () => {
  const params = useParams();
  const location = useLocation();
  const { config, vehicles, isLoading } = useAppData();
  const { selectedStore, availableStores } = useStore();

  // Extract slug from params (can be :id or carro/*, moto/*, caminhao/*)
  const slugOrId = params.id || params['*'] || '';
  const [vehicle, setVehicle] = useState<any>(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [relatedVehicles, setRelatedVehicles] = useState<any[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [relatedSlugs, setRelatedSlugs] = useState<Record<number, string>>({});
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const { colors } = useTheme();
  const { toast } = useToast();

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const textColor = colors.text;

  // Get the store for this vehicle (based on vehicle.store field)
  const vehicleStore = useMemo(() => {
    if (!vehicle) {
      return selectedStore || config.stores[0];
    }
    const storeId = vehicle.store;
    if (!storeId) {
      return selectedStore || config.stores[0];
    }
    return config.stores?.find((s: any) => s._id === storeId) || config.stores[0];
  }, [vehicle, config.stores, selectedStore]);

  // Request user location only when needed (user interaction)
  // Don't request automatically on page load to avoid trust issues

  useEffect(() => {
    const loadVehicleData = async () => {
      // Only check for vehicle after data has loaded
      if (!isLoading && vehicles.length > 0) {
        // Extract ID from slug or use direct ID
        const vehicleId = await extractIdFromSlug(slugOrId || '') || slugOrId;
        const foundVehicle = vehicles.find(v => v.id.toString() === vehicleId);
    if (foundVehicle) {
      setVehicle(foundVehicle);
      
          // Track vehicle view
          trackVehicleView({
            id: foundVehicle.id,
            brand: foundVehicle.brand,
            model: foundVehicle.model,
            version: foundVehicle.version || '',
            year: `${foundVehicle.manufactureYear}/${foundVehicle.modelYear}`,
            price: foundVehicle.price,
            mileage: foundVehicle.mileage
          });

          // Register vehicle as recently viewed
          try {
            const viewedStr = await secureGetItem('recentlyViewedVehicles');
            let viewedVehicles = viewedStr ? JSON.parse(viewedStr) : [];

            // Remove if already exists (to avoid duplicates)
            viewedVehicles = viewedVehicles.filter((v: any) => v.id !== foundVehicle.id);

            // Add to beginning of array with timestamp
            viewedVehicles.unshift({
              id: foundVehicle.id,
              timestamp: Date.now()
            });

            // Keep only last 20 vehicles
            viewedVehicles = viewedVehicles.slice(0, 20);

            await secureSetItem('recentlyViewedVehicles', JSON.stringify(viewedVehicles));

            // Dispatch event to notify other components
            window.dispatchEvent(new Event('vehicleViewed'));
          } catch (error) {
            console.error('Erro ao registrar veículo visualizado:', error);
          }

          // Check if vehicle is favorited
          const favorites = await secureGetItem('favorites');
          if (favorites && Array.isArray(favorites)) {
            setIsFavorited(favorites.includes(foundVehicle.id));
          }

          // Determine if we should filter by store based on unifiedAds and unifiedAdsStore
          const unifiedAds = config?.unifiedAds !== false;
          const unifiedAdsStore = config?.unifiedAdsStore;
          const vehicleStoreId = foundVehicle.store;

          let filterByStore = false;

          if (!unifiedAds) {
            // Rule 1: unifiedAds is false → always filter by vehicle's store
            filterByStore = true;
          } else if (unifiedAdsStore !== null && unifiedAdsStore !== undefined) {
            // Rule 3 & 4: unifiedAds is true and unifiedAdsStore is set
            // Only show all vehicles if vehicle's store is the unifiedAdsStore
            filterByStore = vehicleStoreId !== unifiedAdsStore;
          }
          // Rule 2: unifiedAds is true and unifiedAdsStore is null → don't filter (filterByStore = false)

          // Weighted recommendations: same model + brand + price/attributes, with diversity
          const related = getRelatedVehicles(foundVehicle, vehicles, {
            limit: 4,
            filterByStore,
            vehicleStoreId,
            maxSameModel: 2,
          });

      setRelatedVehicles(related);
    }
        setHasLoaded(true);
      }
    };

    loadVehicleData();
  }, [slugOrId, vehicles, isLoading]);

  // Load slugs for related vehicles
  useEffect(() => {
    const loadRelatedSlugs = async () => {
      const newSlugs: Record<number, string> = {};
      for (const relatedVehicle of relatedVehicles) {
        newSlugs[relatedVehicle.id] = await generateVehicleSlug(relatedVehicle);
      }
      setRelatedSlugs(newSlugs);
    };
    if (relatedVehicles.length > 0) {
      loadRelatedSlugs();
    }
  }, [relatedVehicles]);

  // Show loading state
  if (isLoading || !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  // Show not found only after loading is complete
  if (!vehicle && hasLoaded) {
    return (
      <VehicleNotFound
        config={config}
        buttonColor={buttonColor}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        titleColor={titleColor}
        textColor={textColor}
      />
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const formatMileage = (mileage: number) => {
    if (mileage === 0) return 'Zero KM';
    return new Intl.NumberFormat('pt-BR').format(mileage) + ' km';
  };

  const toggleFavorite = async () => {
    const favorites = await secureGetItem('favorites') || [];
    let updatedFavorites = Array.isArray(favorites) ? [...favorites] : [];

    if (updatedFavorites.includes(vehicle.id)) {
      // Remove from favorites
      updatedFavorites = updatedFavorites.filter((id: number) => id !== vehicle.id);
      setIsFavorited(false);
    } else {
      // Add to favorites
      updatedFavorites.push(vehicle.id);
      setIsFavorited(true);
      
      // Track add to favorites
      trackAddToFavorites({
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage
      });
    }

    await secureSetItem('favorites', updatedFavorites);

    // Dispatch custom event to update other components
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const handleWhatsApp = () => {
    const adLead = vehicleStore.ad?.lead;

    // Check if lead type is "message" and has custom template
    if (adLead?.type === 'message' && adLead?.ios) {
      let message = adLead?.ios;
      message = message.substr(message.indexOf('&text=') + 6); // Extract message after 'text='

      // Replace dynamic placeholders based on replace array
      if (adLead.replace && Array.isArray(adLead.replace)) {
        adLead.replace.forEach((replaceItem: any) => {
          Object.keys(replaceItem).forEach((placeholder) => {
            const field = replaceItem[placeholder];
            let value = '';

            // Map field names to vehicle properties
            switch (field) {
              case 'vehicle':
                value = `${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}`;
                break;
              case 'price':
                value = formatPrice(vehicle.price);
                break;
              case 'brand':
                value = vehicle.brand;
                break;
              case 'model':
                value = vehicle.model;
                break;
              case 'version':
                value = vehicle.version;
                break;
              case 'year':
              case 'modelYear':
                value = vehicle.modelYear.toString();
                break;
              case 'mileage':
                value = `${vehicle.mileage.toLocaleString('pt-BR')} km`;
                break;
              case 'color':
                value = vehicle.color;
                break;
              case 'fuel':
                value = vehicle.fuel;
                break;
              case 'transmission':
                value = vehicle.transmission;
                break;
              default:
                // Try to get value directly from vehicle object
                value = vehicle[field] || '';
            }

            // Replace placeholder in message (case insensitive)
            const regex = new RegExp(`\\{${placeholder}\\}`, 'gi');
            message = message.replace(regex, value);
          });
        });
      }

      // Extract phone number from whatsapp?.ios URL
      const phoneMatch = vehicleStore.ad?.lead?.ios?.match(/phone=([^&]+)/);
      const phone = phoneMatch ? phoneMatch[1] : '';

      // Build WhatsApp URL
      const url = `whatsapp://send?phone=${phone}&text=${encodeURIComponent(message)}`;

      // Track WhatsApp click
      trackWhatsAppClick('vehicle_detail', {
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage
      });

      // Open WhatsApp with fallback to web version
      openWhatsApp(url);
    } else {
      // Default behavior (fallback)
      const message = `Olá,\n\nTenho interesse no veículo abaixo e gostaria de receber mais informações.\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version}, ${formatPrice(vehicle.price)}\n\nFico no aguardo,`;
      const url = `${vehicleStore.ad?.lead?.ios}&text=${encodeURIComponent(message)}`;

      // Track WhatsApp click
      trackWhatsAppClick('vehicle_detail', {
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage
      });

      // Open WhatsApp with fallback to web version
      openWhatsApp(url);
    }
  };

  // Find financing service from vehicle's store
  const financingService = vehicleStore?.services?.find(
    (s: any) => s.service === '65dcb7898eca108d87c91dbc'
  );

  // Find test-drive service from vehicle's store
  const testDriveService = vehicleStore?.services?.find(
    (s: any) => s.service === '65dcb5e78eca108d87c91db8'
  );

  // Check if test-drive should be shown
  const showTestDrive = vehicleStore?.ad?.showTestDriveOption && testDriveService;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${vehicle.brand} ${vehicle.model} ${vehicle.version}`,
          text: `Confira este ${vehicle.brand} ${vehicle.model} por ${formatPrice(vehicle.price)}`,
          url: window.location.href,
        });
      } catch (err) { }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: '✅ Link copiado',
        description: 'O link do veículo foi copiado para a área de transferência.',
      });
    }
  };

  const shareUrl = `${window.location.origin}${location.pathname}${location.search}`;
  const canonicalVehicleUrl = `${window.location.origin}${location.pathname}`;
  const seoVehicleTitle = `${vehicle.brand} ${vehicle.model}${vehicle.version ? ` ${vehicle.version}` : ''} ${vehicle.modelYear}`;
  const hasDiscount = vehicle.originalPrice && vehicle.originalPrice > vehicle.price;
  const { headerBrandSrc: storeLogoSrc } = getStoreBrandAssets(config, vehicleStore);

  const getVehicleTypeLabel = (type?: number) => {
    switch (type) {
      case 2: return 'Moto';
      case 3: return 'Caminhão';
      default: return 'Carro';
    }
  };

  return (
    <Layout5Root withHeader className="bg-background">
      <Header config={config} />
      <SEOHead
        title={seoVehicleTitle}
        description={`${vehicle.brand} ${vehicle.model}${vehicle.version ? ` ${vehicle.version}` : ''} ${vehicle.modelYear} — ${vehicle.color} — ${vehicle.manufactureYear}/${vehicle.modelYear} — ${vehicle.fuel} — ${vehicle.transmission} — ${vehicle.mileage.toLocaleString('pt-BR')} km — ${formatPrice(vehicle.price)}`}
        keywords={`${vehicle.brand}, ${vehicle.model}, ${vehicle.modelYear}, ${vehicle.fuel}, ${vehicle.transmission}, ${vehicle.color}, veículo usado, carro seminovo`}
        image={vehicle.photos?.[0]}
        imageAlt={seoVehicleTitle}
        type="product"
        url={shareUrl}
        canonicalUrl={canonicalVehicleUrl}
        structuredData={generateVehicleStructuredData({
          vehicle,
          config,
          store: vehicleStore,
          vehiclePageUrl: shareUrl
        })}
      />

      <section className="wl5-detail-head">
        <div className="wl5-container">
          <div className="wl5-detail-head-toolbar">
            <nav className="wl5-detail-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Início</Link>
              <span>/</span>
              <Link to="/estoque">Estoque</Link>
              <span>/</span>
              <span className="wl5-detail-breadcrumb-current">{vehicle.brand} {vehicle.model}</span>
            </nav>
          </div>

          <div className="wl5-detail-head-row">
            <div>
              <h1 className="wl5-detail-title" style={{ color: titleColor }}>
                {vehicle.brand} {vehicle.model} {vehicle.modelYear}
              </h1>
              {vehicle.version && (
                <p className="wl5-detail-version" style={{ color: textColor }}>{vehicle.version}</p>
              )}
            </div>
            <div className="wl5-detail-head-side">
              <div>
                {hasDiscount && (
                  <span className="wl5-detail-price-old">{formatPrice(vehicle.originalPrice)}</span>
                )}
                <span className="wl5-detail-price">{formatPrice(vehicle.price)}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="wl5-detail-body">
        <div className="wl5-container">
          <div className="wl5-detail-grid">
            <div className="wl5-detail-main">
              <div className="wl5-detail-panel wl5-detail-gallery-wrap">
                <VehicleGallery
                  key={vehicle.id}
                  photos={vehicle.photos}
                  alt={`${vehicle.brand} ${vehicle.model}`}
                  isZeroKm={vehicle.isZeroKm}
                  isFavorited={isFavorited}
                  onFavorite={toggleFavorite}
                  onShare={handleShare}
                />
              </div>

              <div className="wl5-detail-panel">
                <div className="wl5-detail-panel-head">
                  <h2 className="wl5-detail-panel-title">Visão geral</h2>
                </div>
                <div className="wl5-detail-panel-body !p-0">
                  <div className="wl5-detail-overview">
                    {(vehicle.manufactureYear || vehicle.modelYear) && (
                      <div className="wl5-detail-overview-item">
                        <span className="wl5-detail-overview-label">Ano</span>
                        <span className="wl5-detail-overview-value">{vehicle.manufactureYear}/{vehicle.modelYear}</span>
                      </div>
                    )}
                    {vehicle.mileage !== undefined && vehicle.mileage !== null && (
                      <div className="wl5-detail-overview-item">
                        <span className="wl5-detail-overview-label">Quilometragem</span>
                        <span className="wl5-detail-overview-value">{formatMileage(vehicle.mileage)}</span>
                      </div>
                    )}
                    {vehicle.fuel && (
                      <div className="wl5-detail-overview-item">
                        <span className="wl5-detail-overview-label">Combustível</span>
                        <span className="wl5-detail-overview-value">{vehicle.fuel}</span>
                      </div>
                    )}
                    {vehicle.type !== 2 && vehicle.transmission && (
                      <div className="wl5-detail-overview-item">
                        <span className="wl5-detail-overview-label">Câmbio</span>
                        <span className="wl5-detail-overview-value">{vehicle.transmission}</span>
                      </div>
                    )}
                    {vehicle.color && (
                      <div className="wl5-detail-overview-item">
                        <span className="wl5-detail-overview-label">Cor</span>
                        <span className="wl5-detail-overview-value">{vehicle.color}</span>
                      </div>
                    )}
                    {vehicle.type !== 2 && vehicle.doors && (
                      <div className="wl5-detail-overview-item">
                        <span className="wl5-detail-overview-label">Portas</span>
                        <span className="wl5-detail-overview-value">{vehicle.doors}</span>
                      </div>
                    )}
                    <div className="wl5-detail-overview-item">
                      <span className="wl5-detail-overview-label">Tipo</span>
                      <span className="wl5-detail-overview-value">{getVehicleTypeLabel(vehicle.type)}</span>
                    </div>
                    {availableStores.length > 1 && vehicle.store && (() => {
                      const storeForVehicle = config.stores?.find((s: any) => s._id === vehicle.store);
                      return storeForVehicle ? (
                        <div className="wl5-detail-overview-item">
                          <span className="wl5-detail-overview-label">Loja</span>
                          <span className="wl5-detail-overview-value">{storeForVehicle.company}</span>
                        </div>
                      ) : null;
                    })()}
                  </div>

                  {vehicle.statistics && (vehicle.statistics.financing || vehicle.statistics.favorites) && (
                    <div className="wl5-detail-stats">
                      {vehicle.statistics.financing && config.stores?.[0]?.ad?.displayNumberOfFinancingProposals && (
                        <span className="wl5-detail-stats-item">
                          <FilePen className="h-4 w-4" />
                          {vehicle.statistics.financing > 1 ? `${vehicle.statistics.financing} propostas em análise` : `${vehicle.statistics.financing} proposta em análise`}
                        </span>
                      )}
                      {vehicle.statistics.favorites && vehicleStore?.ad?.displayNumberOfInterestedCustomers && (
                        <span className="wl5-detail-stats-item">
                          <Heart className="h-4 w-4" />
                          {vehicle.statistics.favorites > 1 ? `${vehicle.statistics.favorites} clientes interessados` : `${vehicle.statistics.favorites} cliente interessado`}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {vehicle.description && (
                <div className="wl5-detail-panel">
                  <div className="wl5-detail-panel-head">
                    <h2 className="wl5-detail-panel-title">Descrição</h2>
                  </div>
                  <div className="wl5-detail-panel-body">
                    <p className="wl5-detail-desc">{vehicle.description}</p>
                  </div>
                </div>
              )}

              {vehicle.optionals && vehicle.optionals.length > 0 && (
                <div className="wl5-detail-panel">
                  <div className="wl5-detail-panel-head">
                    <h2 className="wl5-detail-panel-title">Opcionais ({vehicle.optionals.length})</h2>
                  </div>
                  <div className="wl5-detail-panel-body">
                    <div className="wl5-detail-features">
                      {vehicle.optionals.map((optional: string, index: number) => (
                        <div key={index} className="wl5-detail-feature">
                          <span className="wl5-detail-feature-dot" />
                          <span>{optional}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {financingService && financingService.lead?.type === 'message' && (
                <div className="wl5-detail-panel">
                  <div className="wl5-detail-panel-body">
                    <ServiceModal
                      service={financingService}
                      serviceTitle="Simular Financiamento"
                      config={config}
                      renderInline={true}
                      preSelectedVehicle={vehicle}
                    />
                  </div>
                </div>
              )}

              {financingService && (financingService.lead?.type === 'api' || financingService.lead?.type === 'email') && (
                <div className="wl5-detail-panel">
                  <div className="wl5-detail-panel-body">
                    <LeadForm
                      vehicle={vehicle}
                      config={config}
                      buttonColor={buttonColor}
                      store={vehicleStore}
                      serviceType="65dcb7898eca108d87c91dbc"
                      serviceTitle="Simular Financiamento"
                      service={financingService}
                    />
                  </div>
                </div>
              )}
            </div>

            <aside className="wl5-detail-sidebar">
              <div className="wl5-detail-dealer">
                <div className="wl5-detail-dealer-head">
                  <div className="wl5-detail-dealer-avatar">
                    {storeLogoSrc ? (
                      <img src={storeLogoSrc} alt={vehicleStore?.company || config.name} />
                    ) : (
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="wl5-detail-dealer-name">{vehicleStore?.company || config.name}</h3>
                    <p className="wl5-detail-dealer-role">Proprietário do anúncio</p>
                  </div>
                </div>

                {vehicleStore?.place?.address && (
                  <div className="wl5-detail-dealer-address">
                    <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{vehicleStore.place.address}</span>
                    {availableStores.length > 1 && userLocation && (() => {
                      const distance = getDistanceToStore(userLocation, vehicleStore);
                      return distance !== null ? (
                        <Badge
                          variant="secondary"
                          className="ml-auto flex items-center gap-1 px-2 py-0.5 text-white font-medium shrink-0"
                          style={{ backgroundColor: primaryColor }}
                        >
                          <Navigation className="h-3 w-3" />
                          <span className="text-xs">{formatDistance(distance)}</span>
                        </Badge>
                      ) : null;
                    })()}
                  </div>
                )}

                <div className="wl5-detail-cta-stack">
                  {(vehicleStore.ad?.lead?.type === 'api' || vehicleStore.ad?.lead?.type === 'email') ? (
                    <LeadForm
                      vehicle={vehicle}
                      config={config}
                      buttonColor={buttonColor}
                      store={vehicleStore}
                      serviceType="message"
                      serviceTitle="Envie uma mensagem"
                      service={{ lead: vehicleStore.ad.lead }}
                    />
                  ) : (
                    <Button
                      className="w-full text-white"
                      style={{
                        background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                      }}
                      onClick={handleWhatsApp}
                      size="lg"
                    >
                      <MessageCircle className="h-5 w-5 mr-2" />
                      Enviar mensagem
                    </Button>
                  )}

                  <Button
                    onClick={() => {
                      if (vehicleStore?.whatsapp?.ios) {
                        openWhatsApp(vehicleStore.whatsapp?.ios);
                      }
                    }}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    size="lg"
                  >
                    <MessageCircle className="h-5 w-5 mr-2" />
                    WhatsApp
                  </Button>

                  <Button
                    onClick={() => {
                      if (vehicleStore?.phone?.ios) {
                        window.open(vehicleStore.phone?.ios, '_blank');
                      }
                    }}
                    className="w-full text-white"
                    style={{
                      background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                    }}
                    size="lg"
                  >
                    <Phone className="h-5 w-5 mr-2" />
                    Ligar agora
                  </Button>

                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full border-2"
                    style={{
                      borderColor: `${primaryColor}40`,
                      color: primaryColor,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = primaryColor;
                      e.currentTarget.style.backgroundColor = `${primaryColor}10`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = `${primaryColor}40`;
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    onClick={() => {
                      const place = vehicleStore?.place;
                      openStoreDirections(place);
                    }}
                  >
                    <MapPin className="h-5 w-5 mr-2" />
                    Como chegar
                  </Button>
                </div>
              </div>

              {showTestDrive && (
                <div className="wl5-detail-panel">
                  <div className="wl5-detail-panel-body wl5-detail-testdrive">
                    <h3 className="wl5-detail-testdrive-title">
                      <Gauge className="h-5 w-5" style={{ color: buttonColor }} />
                      Test-Drive
                    </h3>
                    <ServiceModal
                      service={testDriveService}
                      serviceTitle="Test-Drive"
                      config={config}
                      preSelectedVehicle={vehicle}
                    >
                      <Button
                        className="w-full text-white"
                        style={{
                          background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                        }}
                      >
                        Agendar test-drive
                      </Button>
                    </ServiceModal>
                    <p className="wl5-detail-testdrive-desc">
                      Agende um test drive e experimente o veículo dos seus sonhos
                    </p>
                  </div>
                </div>
              )}

              <div
                className="wl5-detail-guarantee"
                style={{
                  background: `linear-gradient(135deg, ${secondaryColor}, ${secondaryColor}dd)`,
                }}
              >
                <Shield className="h-10 w-10 mx-auto opacity-90" />
                <h3>Garantia e qualidade</h3>
                <p>
                  Todos os nossos veículos passam por rigorosa inspeção e vêm com garantia.
                </p>
              </div>
            </aside>
          </div>

          {vehicleStore?.ad?.showRelatedOffers && relatedVehicles.length > 0 && (
            <section className="wl5-detail-related">
              <div className="wl5-section-header !mb-5">
                <div>
                  <p className="wl5-eyebrow">Veículos similares</p>
                  <h2 className="wl5-h2">Você também pode gostar</h2>
                </div>
              </div>
              <div className="wl5-detail-related-grid">
                {relatedVehicles.slice(0, 4).map((relatedVehicle) => (
                  <LayoutVehicleCard key={relatedVehicle.id} vehicle={relatedVehicle} />
                ))}
              </div>
            </section>
          )}

          {vehicleStore?.ad?.warning && (
            <div className="wl5-detail-warning">
              <h3>Aviso</h3>
              <p>{vehicleStore.ad.warning}</p>
            </div>
          )}
        </div>
      </section>

      <Footer config={config} />
    </Layout5Root>
  );
};

export default VehicleDetail;